import React, { useEffect, useMemo, useState } from "react";
import { fetchMatkaBetBatch, fetchMatkaBets } from "../../../api/services/matka";
import { formatDisplayDate } from "./MatkaDailyResults";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "cancelled", label: "Cancelled" },
];

function readError(error, fallback) {
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  return fallback;
}

function titleCase(value) {
  const text = String(value || "")
    .replace(/_/g, " ")
    .trim();
  if (!text) return "—";
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

function displayText(value, fallback = "—") {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
}

function formatBetDate(value) {
  if (!value) return "—";
  const formatted = formatDisplayDate(String(value).slice(0, 10));
  return formatted || "—";
}

function formatMoney(amount, currency = "INR") {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `₹${value.toLocaleString("en-IN")}`;
  }
}

function formatSelection(selection) {
  if (selection == null || selection === "") return "—";
  if (typeof selection !== "object") return String(selection);
  const parts = Object.entries(selection)
    .filter(([, value]) => value != null && value !== "")
    .map(([key, value]) => {
      if (typeof value === "object") return formatSelection(value);
      return `${titleCase(key)} ${value}`;
    })
    .filter((part) => part && part !== "—");
  return parts.length ? parts.join(" · ") : "—";
}

function statusClass(status) {
  const value = String(status || "").toLowerCase();
  if (value === "won") return "is-won";
  if (value === "lost") return "is-lost";
  if (value === "cancelled" || value === "canceled") return "is-cancelled";
  return "is-pending";
}

function betTypeLabel(bet) {
  return displayText(bet?.bet_type_name, titleCase(bet?.bet_type_code));
}

function matchesSearch(bet, query) {
  if (!query) return true;
  const haystack = [
    bet?.market_name,
    bet?.bet_type_name,
    bet?.bet_type_code,
    bet?.sub_type,
    bet?.bet_session,
    bet?.status,
    formatSelection(bet?.selection),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function StatusBadge({ status }) {
  return <span className={`matka-bet-status ${statusClass(status)}`}>{titleCase(status)}</span>;
}

function SummaryItem({ label, value }) {
  return (
    <div className="matka-bet-summary-item">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function BetHistoryDetail({ batchId, onBack }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!batchId) return undefined;

    let active = true;
    setLoading(true);
    setError("");
    setBatch(null);

    fetchMatkaBetBatch(batchId)
      .then((data) => {
        if (active) setBatch(data);
      })
      .catch((err) => {
        if (!active) return;
        setBatch(null);
        setError(readError(err, "Unable to load this bet."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [batchId]);

  const bets = Array.isArray(batch?.bets) ? batch.bets : [];
  const currency = batch?.balance?.currency || "INR";
  const betType = displayText(batch?.bet_type?.name, titleCase(batch?.bet_type?.code));

  return (
    <div className="matka-results-panel">
      <button type="button" className="matka-back-to-market-link d-inline-flex align-items-center mb-3" onClick={onBack}>
        <i className="bi bi-arrow-left me-1"></i>
        Back to bet history
      </button>

      {loading ? (
        <div className="matka-empty-state text-center py-5">
          <p className="text-light fs-5">Loading bet...</p>
        </div>
      ) : error ? (
        <div className="matka-empty-state text-center py-5">
          <p className="matka-sangam-error fs-5">{error}</p>
        </div>
      ) : (
        <div className="matka-bids-list-card">
          <h3 className="matka-bids-list-title">
            {displayText(batch?.market?.name, "Bet")} · {betType}
          </h3>
          <div className="matka-bet-summary">
            <SummaryItem label="Session" value={titleCase(batch?.session)} />
            <SummaryItem label="Date" value={formatBetDate(batch?.bet_date)} />
            <SummaryItem label="Status" value={titleCase(batch?.status)} />
            <SummaryItem label="Selections" value={displayText(batch?.selections_count, String(bets.length))} />
            <SummaryItem label="Total amount" value={formatMoney(batch?.total_amount, currency)} />
            <SummaryItem label="Total payout" value={formatMoney(batch?.total_payout, currency)} />
            {batch?.balance ? (
              <SummaryItem
                label="Balance"
                value={`${formatMoney(batch.balance.before, currency)} → ${formatMoney(batch.balance.after, currency)}`}
              />
            ) : null}
          </div>
          {bets.length ? (
            <div className="matka-results-table-wrap">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th>Selection</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Payout</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.map((bet) => (
                    <tr key={bet.id}>
                      <td className="td-panna">{formatSelection(bet.selection)}</td>
                      <td className="td-type">
                        {bet.sub_type ? titleCase(bet.sub_type) : betTypeLabel(bet)}
                      </td>
                      <td className="td-points">{formatMoney(bet.amount, currency)}</td>
                      <td className="td-points">{formatMoney(bet.payout_amount, currency)}</td>
                      <td>
                        <StatusBadge status={bet.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="matka-empty-state text-center py-5">
              <p className="text-light fs-5">No selections in this bet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MatkaBetHistory({ searchTerm = "" }) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [bets, setBets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [batchId, setBatchId] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    fetchMatkaBets({ page, limit: PAGE_SIZE, status: status || undefined })
      .then((data) => {
        if (!active) return;
        setBets(data.bets);
        setPagination(data.pagination);
      })
      .catch((err) => {
        if (!active) return;
        setBets([]);
        setPagination(null);
        setError(readError(err, "Unable to load bet history."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [page, status]);

  const query = searchTerm.trim().toLowerCase();
  const visibleBets = useMemo(
    () => bets.filter((bet) => matchesSearch(bet, query)),
    [bets, query]
  );

  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.page || page;

  if (batchId) {
    return <BetHistoryDetail batchId={batchId} onBack={() => setBatchId("")} />;
  }

  return (
    <div className="matka-results-panel">
      <div className="matka-results-toolbar">
        <div className="matka-results-field">
          <label className="matka-panna-bulk-label" htmlFor="matka-bet-status">
            Status
          </label>
          <div className="matka-panna-bulk-select-box">
            <select
              id="matka-bet-status"
              className="matka-panna-bulk-select"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <i className="bi bi-chevron-down matka-panna-bulk-arrow"></i>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="matka-empty-state text-center py-5">
          <p className="text-light fs-5">Loading bet history...</p>
        </div>
      ) : error ? (
        <div className="matka-empty-state text-center py-5">
          <p className="matka-sangam-error fs-5">{error}</p>
        </div>
      ) : (
        <div className="matka-bids-list-card">
          <h3 className="matka-bids-list-title">My bets</h3>
          {visibleBets.length ? (
            <div className="matka-results-table-wrap">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th>Market</th>
                    <th>Type</th>
                    <th>Selection</th>
                    <th>Session</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBets.map((bet) => (
                    <tr
                      key={bet.id}
                      className="matka-bet-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => setBatchId(bet.batch_id || bet.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setBatchId(bet.batch_id || bet.id);
                        }
                      }}
                    >
                      <td className="td-panna">{displayText(bet.market_name)}</td>
                      <td className="td-type">{betTypeLabel(bet)}</td>
                      <td className="td-panna">{formatSelection(bet.selection)}</td>
                      <td className="td-type">{titleCase(bet.bet_session)}</td>
                      <td className="td-type">{formatBetDate(bet.bet_date)}</td>
                      <td className="td-points">{formatMoney(bet.amount)}</td>
                      <td>
                        <StatusBadge status={bet.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="matka-empty-state text-center py-5">
              <p className="text-light fs-5">
                {query ? `No bets found matching "${searchTerm.trim()}"` : "No bets yet."}
              </p>
            </div>
          )}
          {pagination && pagination.total > 0 ? (
            <div className="matka-bets-pager">
              <button
                type="button"
                disabled={!pagination.hasPrev || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={!pagination.hasNext || loading}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
